import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {IsGranted} from '../../../../decorator/is-granted';
import {MetaTitle} from '../../../../decorator/meta-title';
import {Partner} from '../../../../models/partner/partner';
import {ConversionEvent} from '../../../../models/conversion-event';
import {Paginator} from '../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {LoaderService} from '../../../../services/loader/loader.service';
import {PartnerService} from '../../../../services/partner/partner.service';
import {ConversionEventService} from '../../../../services/conversion-event/conversion-event.service';
import { NgClass } from '@angular/common';
import {ParisDatePipe} from '../../../../pipes/date/paris-date.pipe';
import {FormsModule} from '@angular/forms';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {TableSorterHeaderDirective} from '../../../../directives/table-sorter/table-sorter-header.directive';
import {TableSorterDirective} from '../../../../directives/table-sorter/table-sorter.directive';
import {MoneyPipe} from '../../../../pipes/money/money.pipe';
import {QueryFilterService} from '../../../../services/query-filter/query-filter.service';
import {ClickDetailComponent} from '../click-log/click-detail/click-detail.component';
import {LeadDetailComponent} from '../lead-log/lead-detail/lead-detail.component';
import {DialogService} from '../../../../services/dialog/dialog.service';
import {ConversionDetailComponent} from '../conversion-log/conversion-detail/conversion-detail.component';
import {DisplayTableColumnComponent} from '../../../layout/display-table-column/display-table-column.component';
import {LogFiltersComponent} from '../../../layout/log-filters/log-filters.component';
import {LogFilter} from '../../../../types/log-filter';
import {LogForm} from '../../../../forms/log-form';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {getTableHeaders} from '../../../../helpers/table.helper';
import {CsvExportService} from '../../../../services/csv-export/csv-export.service';
import {SnackbarService} from '../../../../services/snackbar/snackbar.service';
import {CsvExportProgressComponent} from '../../../layout/csv-export-progress/csv-export-progress.component';

@Component({
  selector: 'app-conversion-event-log',
  imports: [
    ParisDatePipe,
    FormsModule,
    PaginatorComponent,
    TableSorterHeaderDirective,
    TableSorterDirective,
    NgClass,
    MoneyPipe,
    DisplayTableColumnComponent,
    LogFiltersComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    CsvExportProgressComponent,
  ],
  templateUrl: './conversion-event-log.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './conversion-event-log.component.scss'
})
@IsGranted(['CONVERSION_EVENT_VIEW'])
@MetaTitle('Conversion event logs')
export class ConversionEventLogComponent implements OnInit {
  partners: Partner[] = [];
  conversionEvents: ConversionEvent[] = [];
  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('createdAt', 'desc');
  openConversionEvent: ConversionEvent | null = null;

  filters: LogFilter = LogForm.initFilters({
    cid: '',
    name: [],
    status: null
  });

  displayedColumns = {
    clickid: false,
    lead: false,
    conversion: false,
    amount: false
  };

  statusList: string[] = ['complete', 'failed', 'pending', 'error'];
  eventNameList: string[] = ['Oktopus Lead', 'Oktopus Lead AR', 'Oktopus Sale', 'Oktopus Double Optin', 'Oktopus Checkout'];

  downloading: boolean = false;
  exportProgress: { current: number; total: number } | null = null;

  constructor(
    private loader: LoaderService,
    private partnerService: PartnerService,
    private conversionEventService: ConversionEventService,
    private queryFilterService: QueryFilterService,
    private dialog: DialogService,
    private csvExportService: CsvExportService,
    private snackbar: SnackbarService,
  ) {
  }

  ngOnInit(): void {
    this.queryFilterService.init(this.paginator, this.tableSorter);

    this.filters = this.queryFilterService.initFilters();
    this.filters['cid'] = this.queryFilterService.getFilter('cid');
    this.filters['name'] = this.queryFilterService.getFilter('name')?.split(',') ?? [];
    this.filters['status'] = this.queryFilterService.getFilter('status') ?? null;

    this.loader.startLoading();
  }

  applyFilters(): void {
    this.paginator.currentPage = 1;
    this.fetchAll();
  }

  fetchAll(): void {
    this.loader.startLoading();

    const params: any = this.getParameters();

    this.queryFilterService.setFilters(params);

    this.conversionEventService.findAll(params, this.paginator, this.tableSorter).subscribe(conversionEvents => {
      this.conversionEvents = conversionEvents;
      this.loader.stopLoading();
    });
  }

  getStatusClass(status: string | null): string {
    switch (status) {
      case 'complete':
        return 'text-bg-success';
      case 'pending':
        return 'text-bg-warning';
      default:
        return 'text-bg-danger';
    }
  }

  toggleConversionEvent(conversionEvent: ConversionEvent): void {
    if (this.openConversionEvent !== conversionEvent) {
      this.openConversionEvent = conversionEvent;
    } else {
      this.openConversionEvent = null;
    }
  }

  openClickDetail(conversionEvent: ConversionEvent): void {
    if (conversionEvent.clickId) {
      this.dialog.openDialog(ClickDetailComponent, {
        class: 'modal-lg',
        data: {
          id: conversionEvent.clickId
        }
      }).subscribe(() => {
      });
    }
  }

  openLeadDetail(conversionEvent: ConversionEvent): void {
    if (conversionEvent.leadId) {
      this.dialog.openDialog(LeadDetailComponent, {
        class: 'modal-lg',
        data: {
          id: conversionEvent.leadId
        }
      }).subscribe(() => {
      });
    }
  }

  openConversionDetail(conversionEvent: ConversionEvent): void {
    if (conversionEvent.conversionId) {
      this.dialog.openDialog(ConversionDetailComponent, {
        class: 'modal-lg',
        data: {
          id: conversionEvent.conversionId,
        }
      }).subscribe(() => {
      });
    }
  }

  downloadCsv(): void {
    if (this.downloading) {
      return;
    }

    const params = this.getParameters();
    params['headers'] = getTableHeaders('conversion-events');

    if (!params.startDate || !params.endDate) {
      this.snackbar.error('Please select a date range before exporting.');
      return;
    }

    this.downloading = true;
    this.exportProgress = null;

    this.csvExportService.export({
      endpoint: '/conversion-event/download',
      params,
      filename: 'conversion-event-logs.csv'
    }).subscribe({
      next: (event) => {
        if (event.type === 'progress') {
          this.exportProgress = { current: event.current, total: event.total };
        } else {
          this.downloading = false;
          this.exportProgress = null;
          if (event.truncatedChunks > 0) {
            this.snackbar.warning(
              `${event.truncatedChunks} of ${event.chunkCount} chunk(s) were truncated. The export may be incomplete — try a smaller date range.`
            );
          }
        }
      },
      error: () => {
        this.downloading = false;
        this.exportProgress = null;
        this.snackbar.error('CSV export failed. Please try again.');
      }
    });
  }

  private getParameters(): any {
    let params = LogForm.getFilters(this.filters, 'advertisingPlatform,partner');

    if (this.filters['cid']) {
      params.cid = this.filters['cid'];
    }

    if (this.filters['name'] && this.filters['name'].length) {
      params.name = this.filters['name'].join(',');
    }

    if (this.filters['status']) {
      params.status = this.filters['status'];
    }

    return params;
  }
}
